import ReactMarkdown from 'react-markdown';
import { clsx } from 'clsx';
import remarkGfm from 'remark-gfm';

import takeCompleteMarkdown from '../utils/takeCompleteMarkdown';

import type { AnchorHTMLAttributes, ReactElement, TableHTMLAttributes } from 'react';
import type { Components } from 'react-markdown';
import type IAiChatMarkdown from '../interfaces/IAiChatMarkdown';

import styles from '../sass/AiChat.module.scss';

/** Links the model wrote leave the app, so they open in a new tab and hand over no opener. */
function AiChatLink({ href, children }: AnchorHTMLAttributes<HTMLAnchorElement>): ReactElement {
  return (
    <a className={styles.link} href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

/**
 * A table in a scroll box of its own.
 *
 * The box scrolls, not the table: making the table itself the scrolling element means giving it
 * `display: block`, which stops it generating table boxes for its own rows and leaves the cells to
 * fall out of their columns. Wrapping keeps the table a table.
 */
function AiChatTable({ children }: TableHTMLAttributes<HTMLTableElement>): ReactElement {
  return (
    <div className={styles.tableScroll}>
      <table className={styles.table}>{children}</table>
    </div>
  );
}

/** Fixed for the life of the module, so a chunk arriving does not rebuild them. */
const MARKDOWN_PLUGINS = [remarkGfm];
const MARKDOWN_COMPONENTS: Components = { a: AiChatLink, table: AiChatTable };

/**
 * Markdown as the model wrote it, minus whatever it has not finished writing.
 *
 * Raw HTML is escaped rather than rendered — the default, and worth keeping for text a model
 * produces.
 */
function AiChatMarkdown({ text, isStreaming, className }: IAiChatMarkdown): ReactElement {
  // Only text still arriving can be half-written. A finished message is whole, and a marker left
  // unclosed in it was meant literally.
  const markdown = isStreaming ? takeCompleteMarkdown(text) : text;

  return (
    <div className={clsx(styles.markdown, className)}>
      <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={MARKDOWN_COMPONENTS}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

export default AiChatMarkdown;
